/* ----------------------------------------------------------------------------
 * Engine registry — the client-side mirror of the `bc_engines` table
 * (design-brief-v2 §3.3). The rail, IA and routing all render from this list,
 * so lighting up an engine = flipping a status here (and, in prod, the row).
 *
 * `accent` maps to the [data-engine] scope in tokens.css — the single swappable
 * identity edge. `capabilities` drive the per-engine CapabilityNav.
 * Ordering follows the MVP-first rail order in §5.1.
 * -------------------------------------------------------------------------- */

export type EngineStatus = 'ga' | 'beta' | 'partial' | 'coming' | 'reserved';

export interface Capability {
  key: string;
  label: string;
  /** route under the engine base; omitted = not yet routed (preview). */
  path?: string;
  /** nested subnav — rendered indented under this capability in the nav. */
  children?: Capability[];
  /** reserved seam: the honest "why not yet" line rendered in place of a
   *  route — never fake UI, never a dead link. */
  reservedNote?: string;
}

export interface Engine {
  key: string; // matches [data-engine="..."] in tokens.css
  name: string; // "[Domain] Engine" per brand rule §4.1
  short: string; // rail label
  status: EngineStatus;
  icon: string; // glyph placeholder — design assets replace with real iconography
  tagline: string;
  description: string;
  base: string; // route base, e.g. /sales
  capabilities: Capability[];
  /** reserved engines are architecture, not MVP (§3.3). */
  group: 'vertical' | 'cross-engine' | 'reserved';
}

export const ENGINES: Engine[] = [
  {
    key: 'sales',
    name: 'Sales Engine',
    short: 'Sales',
    status: 'ga',
    icon: '◆',
    tagline: 'Find, qualify, contact, convert.',
    description: 'Outreach, a lightweight CRM, pipeline and evidence-backed lead intelligence — one connected motion from prospect to revenue.',
    base: '/sales',
    group: 'vertical',
    capabilities: [
      { key: 'leads', label: 'Leads', path: '/sales/leads' },
      { key: 'contacts', label: 'Contacts', path: '/sales/crm/contacts' },
      { key: 'pipeline', label: 'Pipeline', path: '/sales/pipeline' },
      { key: 'outreach', label: 'Outreach Tracking', path: '/sales/outreach' },
      { key: 'revenue-attribution', label: 'Revenue Attribution', path: '/sales/revenue-attribution' },
      { key: 'campaigns', label: 'Campaigns' },
      { key: 'templates', label: 'Templates' },
      { key: 'suppression', label: 'Suppression' },
    ],
  },
  {
    key: 'seo',
    name: 'SEO Engine',
    short: 'SEO',
    status: 'ga',
    icon: '▲',
    tagline: 'Search visibility, honestly measured.',
    description: 'Keyword research, rank tracking, competitor snapshots and site health over licensed data — always source-attributed, never a proprietary-index claim.',
    base: '/seo',
    group: 'vertical',
    capabilities: [
      // IA regroup (G1/B3): Overview keeps the existing landing; Search
      // Performance and Site Health group the existing surfaces under honest
      // subnavs; Local Growth is a reserved seam, not fake UI.
      { key: 'overview', label: 'Overview', path: '/seo/overview' },
      { key: 'projects', label: 'Projects', path: '/seo/projects' },
      // SEO-D (W4a) executive intelligence layer — FREE, read-only over stored rows.
      { key: 'executive', label: 'Executive Dashboard', path: '/seo/executive' },
      { key: 'alerts', label: 'Alerts', path: '/seo/alerts' },
      { key: 'opportunities', label: 'Opportunities', path: '/seo/opportunities' },
      // Domain intelligence (W1a) — now real: Domain Overview + Organic
      // Keywords are paid (DataForSEO Labs, user-triggered); Rank Tracking is a
      // free read of stored rankings.
      { key: 'domain-overview', label: 'Domain Overview', path: '/seo/domain-overview' },
      { key: 'organic-keywords', label: 'Organic Keywords', path: '/seo/organic-keywords' },
      { key: 'rank-tracking', label: 'Rank Tracking', path: '/seo/rank-tracking' },
      { key: 'keyword-research', label: 'Keyword Research', path: '/seo/keyword-research' },
      { key: 'serp-analysis', label: 'SERP Analysis', path: '/seo/serp-analysis' },
      { key: 'competitor-analysis', label: 'Competitor Analysis', path: '/seo/competitor-analysis' },
      { key: 'competitor-tracking', label: 'Position Tracking', path: '/seo/competitor-tracking' },
      { key: 'domain-comparison', label: 'Domain Comparison', path: '/seo/domain-comparison' },
      { key: 'backlinks', label: 'Backlinks — Beta', path: '/seo/backlinks' },
      { key: 'tech-audit', label: 'Technical Site Audit', path: '/seo/tech-audit' },
      { key: 'map-rank', label: 'Map Rank', path: '/seo/map-rank' },
      {
        key: 'search-performance',
        label: 'Search Performance',
        path: '/seo/search-performance',
        children: [
          {
            key: 'search-console',
            label: 'Google Search Console',
            path: '/seo/search-performance/google-search-console',
          },
          { key: 'keywords', label: 'Keywords', path: '/seo/keywords' },
          { key: 'rankings', label: 'Rankings', path: '/seo/rankings' },
        ],
      },
      {
        key: 'site-health',
        label: 'Site Health',
        path: '/seo/site-health',
        children: [
          { key: 'pagespeed', label: 'PageSpeed', path: '/seo/site-health/pagespeed' },
          {
            key: 'core-web-vitals',
            label: 'Core Web Vitals',
            path: '/seo/site-health/core-web-vitals',
          },
          {
            key: 'technical-issues',
            label: 'Technical Issues',
            path: '/seo/site-health/technical-issues',
          },
        ],
      },
      { key: 'local-growth', label: 'Local Growth — Beta', path: '/seo/local-growth' },
      { key: 'competitors', label: 'Competitors', path: '/seo/competitors' },
      { key: 'spend', label: 'Spend & Budget', path: '/seo/spend' },
    ],
  },
  {
    key: 'marketing',
    name: 'Marketing Engine',
    short: 'Marketing',
    status: 'beta',
    icon: '✦',
    tagline: 'Campaigns, journeys, segmentation.',
    description: 'Email, campaigns, customer journeys, segmentation, landing pages and forms — grounded in the same shared contact.',
    base: '/marketing',
    group: 'vertical',
    capabilities: [
      { key: 'campaigns', label: 'Campaigns', path: '/marketing/campaigns' },
      { key: 'segments', label: 'Segments', path: '/marketing/segments' },
      { key: 'campaign-attribution', label: 'Campaign Attribution', path: '/marketing/campaign-attribution' },
    ],
  },
  {
    key: 'social',
    name: 'Content Engine',
    short: 'Content',
    status: 'beta',
    icon: '❖',
    tagline: 'Create, plan, schedule, engage.',
    description: 'AI-assisted content creation, planning, scheduling and engagement analytics — from blog and social drafts through the calendar. No auto-follow/like/DM automation, ever.',
    base: '/social',
    group: 'vertical',
    capabilities: [
      { key: 'campaigns', label: 'Campaigns' },
      // AI Content Studio — content/blog creation lives with the Content engine.
      { key: 'content', label: 'Content', path: '/social/content-studio' },
      { key: 'studio', label: 'AI Studio', path: '/social/content-studio' },
      { key: 'stock-media', label: 'Stock Media — Beta', path: '/social/content-studio/stock' },
      { key: 'calendar', label: 'Calendar', path: '/social/calendar' },
      { key: 'templates', label: 'Templates', path: '/social/templates' },
      { key: 'providers', label: 'Connections', path: '/social/connections' },
    ],
  },
  {
    key: 'automation',
    name: 'Automation Engine',
    short: 'Automation',
    status: 'coming',
    icon: '⟳',
    tagline: 'Cross-engine workflows, gated by approvals.',
    description: 'Trigger → condition → action across engines, consuming the shared event stream and acting through published module APIs. Approvals gate every write-back.',
    base: '/automation',
    group: 'cross-engine',
    capabilities: [],
  },
  {
    key: 'analytics',
    name: 'Analytics Engine',
    short: 'Analytics',
    status: 'coming',
    icon: '▨',
    tagline: 'Funnels, attribution, forecasting.',
    description: 'The analyst’s workbench: cross-engine dashboards, funnels, attribution and forecasting. Feeds the Executive OS briefing.',
    base: '/analytics',
    group: 'cross-engine',
    capabilities: [],
  },
  {
    key: 'communications',
    name: 'Communications Engine',
    short: 'Comms',
    status: 'partial',
    icon: '✉',
    tagline: 'One timeline per contact.',
    description: 'A unified communication history across channels, keyed on the shared contact — email now, messaging later.',
    base: '/communications',
    group: 'cross-engine',
    capabilities: [],
  },
  {
    key: 'ai',
    name: 'AI Engine',
    short: 'AI',
    status: 'ga',
    icon: '✧',
    tagline: 'The intelligence in the wires.',
    description: 'Both a home (research copilots, model governance, AI audit trail) and a cross-cutting surface callable inside every engine. Advisory, always human-in-control.',
    base: '/ai',
    group: 'cross-engine',
    capabilities: [
      { key: 'copilots', label: 'Copilots' },
      { key: 'insights', label: 'Insights' },
      { key: 'audit', label: 'AI Audit Trail' },
    ],
  },
  {
    key: 'customer-success',
    name: 'Customer Success Engine',
    short: 'Success',
    status: 'reserved',
    icon: '♥',
    tagline: 'Health, retention, renewals.',
    description: 'Reserved. Customer health, retention, renewals, feedback, support and NPS — a four-seam plug-in when it lands.',
    base: '/customer-success',
    group: 'reserved',
    capabilities: [],
  },
  {
    key: 'people',
    name: 'People Engine',
    short: 'People',
    status: 'reserved',
    icon: '☗',
    tagline: 'Recruit, onboard, grow the team.',
    description: 'Reserved. Recruitment, interview pipeline, onboarding and performance — architecture, not MVP.',
    base: '/people',
    group: 'reserved',
    capabilities: [],
  },
  {
    key: 'knowledge',
    name: 'Knowledge Engine',
    short: 'Knowledge',
    status: 'reserved',
    icon: '❑',
    tagline: 'Company memory, made intelligent.',
    description: 'Reserved. Knowledge base, document intelligence, embeddings and AI memory — reserved seam for later.',
    base: '/knowledge',
    group: 'reserved',
    capabilities: [],
  },
];

// Marketplace is a distribution layer, not an engine (§3.6) — surfaced on the
// rail base in a distinct reserved treatment.
export const MARKETPLACE = {
  key: 'marketplace',
  name: 'Marketplace',
  short: 'Marketplace',
  icon: '⊞',
  status: 'reserved' as const,
  base: '/marketplace',
  tagline: 'Agents, templates, recipes, extensions.',
  description: 'Reserved distribution layer — AI agents, templates, automation recipes and integrations over the same four-seam primitives.',
};

export const engineByKey = (key: string): Engine | undefined =>
  ENGINES.find((e) => e.key === key);

export const isLive = (status: EngineStatus): boolean =>
  status === 'ga' || status === 'beta' || status === 'partial';

/** Find the engine that owns a given pathname (for shell context). */
export function engineForPath(pathname: string): Engine | undefined {
  return ENGINES.find((e) => pathname === e.base || pathname.startsWith(e.base + '/'));
}
